import {
  parsePropId,
  PropType,
  propTypeMap,
  SerializeContext,
} from '@packages/systems/code-libraries/propTypes';
import {getCodeModuleId} from '@packages/systems/code-modules/core';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  useCodeModuleWorkerUrlMap,
  useStrictCsp,
  useCodeIslandLayoutCssFile,
  useEntitlements,
  useOpenCanvas,
  usePageId,
  useSite,
  useSiteComponentVersion,
  useRuntimeEnvironment,
  useSupportsStreamingRendering,
  useStyleBlocks,
  useTagCssPath,
  useOptimizationClickMode,
} from '@packages/systems/render';
import {EVENT_IDS_ATOM_FIELD} from '@packages/systems/optimization/constants';
import {
  maybeAddEventIdsFromData,
  maybeAddEventTrackingAttribute,
} from '@packages/systems/optimization/utils';
import {Atom, Plugins, withSystem} from '@packages/systems/wfdl';
import {
  TBoolean,
  TGeneric,
  TListOfElement,
  TRecord,
  TText,
  TTypeReference,
  TUnion,
} from '@packages/systems/wfdl-ast';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {DesignerMode} from '@packages/utilities/environment';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {type Json, getObject} from '@packages/utilities/json';
import {getChildren} from '@packages/utilities/data-utils';
import * as React from 'react';
import {
  isReservedName,
  SLOT_METADATA_PROP,
  type SlotMetadata,
  type SlotMetadataMap,
} from '@webflow/data-types';
import {
  useCodeComponentsNestedRootEnabled,
  useCodeComponentsSafeModeEnabled,
  useCodeComponentsWebflowContext,
  useVariableAliasCss,
  useRenderCheckpoints,
  useSerializeContext,
} from './CodeIslandHooks';
import {
  DISPLAY_CONTENTS_STYLE,
  getCodeIslandDefinitionId,
  getLoader,
  parseNativeIdPath,
  reportDesignerError,
  requestFixWithAI,
  type IRendererPrivateWebflowContext,
} from './CodeIslandSharedUtils';
import {CodeIslandSsr} from './CodeIslandSsr';
import CodeIslandStateCard, {
  CodeIslandStateCardFixAction,
  CodeIslandStateCardReloadAction,
} from './CodeIslandStateCard';
import ShadowRoot from './ShadowRootPolyfill';
import {
  useCodeIslandCustomElementState,
  useCodeIslandLastKnownSize,
  useCodeIslandRefreshCounter,
  useCodeIslandScenarioBinding,
  useReloadCodeIslandCustomElement,
} from './siteModules/CodeIslandCustomElementStoreHooks';
import {
  STATE_SCENARIO_HOST_ATTR,
  STATE_SCENARIO_LABEL_HOST_ATTR,
  STATE_SCENARIO_PROPS_HOST_ATTR,
} from '@packages/systems/code-libraries/component-states';

// this is only used in browser runtime enviroment so this is safe
// eslint-disable-next-line webflow/no-dangerous-imports
import {extractTagCssFromDesignerCanvas} from '@packages/systems/tag-css-browser';
import {useTranslation} from '@packages/systems/designer-translations';

const TAG_CSS_ID = 'webflow-tag-css';
const VARIABLE_ALIASES_ID = 'webflow-variables-aliases';

const LOADING_MESSAGE = 'Loading code component';

type CodeIslandHost = HTMLElement & {
  privateWebflowContext?: IRendererPrivateWebflowContext;
};

type EvaluatedProp = {tag: keyof typeof propTypeMap; val: Json};

function getSlotMetadata(value: EvaluatedProp): SlotMetadata | undefined {
  if (!Array.isArray(value.val) || value.val.length === 0) {
    // An empty or missing array is considered an empty slot
    return {empty: true};
  }
  return undefined;
}

function isEvaluatedProp(maybeProp: Json): maybeProp is EvaluatedProp {
  const obj = getObject(maybeProp);
  return Boolean(
    obj && obj.tag && typeof obj.tag === 'string' && obj.tag in propTypeMap
  );
}

// A new SlotMetadata field must be listed here and return Json.
const slotMetadataFields: Record<
  keyof SlotMetadata,
  (meta: SlotMetadata) => Json | undefined
> = {
  empty: (meta) => meta.empty,
};

function slotMetadataMapToJson(map: SlotMetadataMap): Json {
  const encoded: {[slotName: string]: Json} = {};
  for (const [slotName, meta] of Object.entries(map)) {
    const entry: {[key: string]: Json} = {};
    for (const [key, readField] of Object.entries(slotMetadataFields)) {
      const value = readField(meta);
      if (value !== undefined) entry[key] = value;
    }
    encoded[slotName] = entry;
  }
  return encoded;
}

export function serializeProps(
  propsData: Json,
  context: SerializeContext
): {serializedProps: string; serializedSlots: string} {
  const obj = getObject(propsData) ?? {};
  const {props, slots, slotMetadata} = Object.entries(obj).reduce<{
    props: Record<string, Json>;
    slots: string[];
    slotMetadata: SlotMetadataMap;
  }>(
    (acc, [key, value]) => {
      if (!isEvaluatedProp(value)) return acc;
      const propType = propTypeMap[value.tag];
      const propId = parsePropId(key);
      // If the prop is a reserved name, we skip it
      if (isReservedName(propId)) return acc;
      const serializedResult = propType.serialize(value.val, context);
      // If the prop is not serializable, we skip it
      if (serializedResult !== undefined) acc.props[propId] = serializedResult;
      // If the prop is a slot, we add it to the list of slots
      if (propType.getSlotExpression) acc.slots.push(propId);
      if (value.tag === PropType.Slot) {
        const meta = getSlotMetadata(value);
        if (meta) acc.slotMetadata[propId] = meta;
      }
      return acc;
    },
    {props: {}, slots: [], slotMetadata: {}}
  );
  if (Object.keys(slotMetadata).length > 0) {
    // We add the reserved __wf_slot_metadata prop containing an object
    // with the slot metadata for each slot.
    props[SLOT_METADATA_PROP] = slotMetadataMapToJson(slotMetadata);
  }
  return {
    serializedProps: JSON.stringify(props),
    serializedSlots: JSON.stringify(slots),
  };
}

const CodeIslandRender = Atom.createRender(
  ({
    data,
    generateAttributes,
    attributes,
    classes,
    sharedContext: {hrefMap},
    renderChild,
  }) => {
    const t = useTranslation();
    const runtimeEnvironment = useRuntimeEnvironment();
    const optimizationClickMode = useOptimizationClickMode();
    const trackedAttributes = attributes.withMutations((attrs) => {
      attrs.update(maybeAddEventIdsFromData(runtimeEnvironment, data));
      attrs.update(
        maybeAddEventTrackingAttribute(
          runtimeEnvironment,
          optimizationClickMode
        )
      );
    });
    const attrs = generateAttributes(trackedAttributes, classes);
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const tagStylesEnabled = Boolean(data.options.applyTagSelectors);
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const ssrEnabled = Boolean(data.options.ssr);
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const prerenderEnabled = Boolean(data.options.prerender);
    const nestedRootEnabled = useCodeComponentsNestedRootEnabled();

    const [tagStyles, setTagStyles] = React.useState('');
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const loader = React.useMemo(() => getLoader(data.loader), [data.loader]);
    const siteComponentVersion = useSiteComponentVersion();
    const isOpenCanvas = useOpenCanvas();
    const [isDirectChildOfCanvasFrame, setIsDirectChildOfCanvasFrame] =
      React.useState(false);

    const {symbolId, instanceId: designerInstanceId} = parseNativeIdPath(
      attributes.get('data-wf-id', '[]') as string
    );
    const pageId = usePageId();
    const siteData = useSite();

    const codeIslandState = useCodeIslandCustomElementState(designerInstanceId);
    const reloadCodeIsland =
      useReloadCodeIslandCustomElement(designerInstanceId);
    const refreshCounter = useCodeIslandRefreshCounter(designerInstanceId);
    const lastKnownSize = useCodeIslandLastKnownSize(
      designerInstanceId,
      getCodeIslandDefinitionId(loader)
    );
    const scenarioBinding = useCodeIslandScenarioBinding(designerInstanceId);
    const codeModuleWorkerUrlMap = useCodeModuleWorkerUrlMap();
    const strictCsp = useStrictCsp();
    const codeIslandLayoutCssFile = useCodeIslandLayoutCssFile();
    const supportsStreamingRendering = useSupportsStreamingRendering();

    // this is only present in DesignerContext
    const styleBlocks = useStyleBlocks();
    // this is only present in SsrContext
    const tagCssPath = useTagCssPath();
    const tagStylesUrl = tagStylesEnabled ? tagCssPath : null;

    const moduleId = React.useMemo(() => getCodeModuleId(loader), [loader]);
    const aliasCss = useVariableAliasCss(moduleId);
    const functionsPreviewModuleId =
      loader?.tag === 'SITE' ? (siteData.id ?? null) : moduleId;
    const webflowContext = useCodeComponentsWebflowContext({
      moduleId,
      functionsPreviewModuleId,
    });

    // The private half of the webflow context is handed to the custom element as
    // a JS property rather than an attribute, so it never lands in the DOM. The
    // ref callback below delivers it, because that runs while React is still
    // committing: with the module already cached, `connectedCallback` can reach
    // its first render before passive effects run.
    const hostRef = React.useRef<CodeIslandHost | null>(null);
    const privateContextRef = React.useRef<IRendererPrivateWebflowContext>({});
    privateContextRef.current = webflowContext.privateContext;

    // Delivers later changes, such as a rotated preview token, to an element
    // that is already mounted and so past its ref callback.
    React.useEffect(() => {
      if (hostRef.current) {
        hostRef.current.privateWebflowContext = webflowContext.privateContext;
      }
    }, [webflowContext.privateContext]);

    const setCodeIslandRef = React.useCallback(
      (node: CodeIslandHost | null) => {
        hostRef.current = node;
        if (!node) {
          setIsDirectChildOfCanvasFrame(false);
          return;
        }

        node.privateWebflowContext = privateContextRef.current;

        setIsDirectChildOfCanvasFrame(
          Boolean(node.parentElement?.hasAttribute('data-wf-frame-viewport'))
        );
      },
      []
    );

    const serializeContext = useSerializeContext(hrefMap);
    const {serializedProps, serializedSlots} = React.useMemo(() => {
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      return serializeProps(data.props, serializeContext);
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    }, [data.props, serializeContext]);

    const codeComponentsSafeModeEnabled = useCodeComponentsSafeModeEnabled();

    const shouldParseDesignerTagCss = tagStylesEnabled && Boolean(styleBlocks);

    const handleWindowLoad = React.useCallback(() => {
      if (shouldParseDesignerTagCss) {
        const tagCss = extractTagCssFromDesignerCanvas(document);
        setTagStyles(tagCss);
      }
    }, [shouldParseDesignerTagCss]);

    React.useEffect(() => {
      // Need to parse tag css on window load event in case stylesheets haven't loaded yet
      window.addEventListener('load', handleWindowLoad);
      return () => {
        window.removeEventListener('load', handleWindowLoad);
      };
    }, [handleWindowLoad]);

    React.useEffect(() => {
      if (shouldParseDesignerTagCss) {
        const tagCss = extractTagCssFromDesignerCanvas(document);
        setTagStyles(tagCss);
      }
    }, [shouldParseDesignerTagCss, styleBlocks]); // styleBlocks dependency means this runs every time styles change

    const loaderString = React.useMemo(() => JSON.stringify(loader), [loader]);
    // Serialized wholesale into the data-webflow-context attribute, which
    // anything on the page can read. Safe because the public half of the context
    // cannot hold secrets by type — those travel as a property instead.
    const webflowContextString = React.useMemo(
      () => JSON.stringify(webflowContext.publicContext),
      [webflowContext.publicContext]
    );

    const metadata = React.useMemo(
      () => ({
        pageId,
        symbolId,
        siteId: siteData.id,
        nodeId: designerInstanceId,
        loaderType: loader?.tag || null,
        moduleId: loader?.tag === 'FEDERATION' ? loader.val.moduleId : null,
        submoduleId:
          loader?.tag === 'FEDERATION' ? loader.val.submoduleId : null,
      }),
      [loader, pageId, symbolId, siteData.id, designerInstanceId]
    );

    const analyticsMetadata = React.useMemo(
      () =>
        // analytics metadata is only used in designer and preview mode
        runtimeEnvironment !== WebflowEnvironments.SSR
          ? {
              'data-analytics-metadata': JSON.stringify(metadata),
            }
          : {},

      [metadata, runtimeEnvironment]
    );

    const onRenderError = React.useCallback(() => {
      reportDesignerError(
        'CodeComponentError',
        'Code component render failed',
        metadata
      );
    }, [metadata]);

    const {renderVerified, resetRenderCheckpoints} = useRenderCheckpoints(
      designerInstanceId,
      onRenderError
    );

    const handleCodeIslandStateReload = React.useCallback(() => {
      reloadCodeIsland();
      // clear the render error and reset the checkpoints so we verify the checkpoints again next render
      resetRenderCheckpoints();
    }, [reloadCodeIsland, resetRenderCheckpoints]);

    const previousSiteComponentVersionRef = React.useRef(siteComponentVersion);

    React.useEffect(() => {
      if (previousSiteComponentVersionRef.current === siteComponentVersion) {
        return;
      }

      previousSiteComponentVersionRef.current = siteComponentVersion;

      if (codeIslandState?.status === 'error') {
        reloadCodeIsland();
        resetRenderCheckpoints();
      }
    }, [
      siteComponentVersion,
      codeIslandState?.status,
      reloadCodeIsland,
      resetRenderCheckpoints,
    ]);

    // In SSR, CodeIsland nodes are removed during publishing if lacking entitlements
    // Since we still have access to data afterwards, we render null here as a fallback
    const entitlements = useEntitlements();

    if (
      designerInstanceId &&
      runtimeEnvironment === WebflowEnvironments.DESIGNER &&
      renderVerified === undefined &&
      codeIslandState?.status !== 'error'
    ) {
      // if renderVerified is undefined, we haven't verified the render checkpoints yet so we want to wait before we render anything
      // render checkpoints are only used if we are in the designer
      return null;
    }

    if (!entitlements?.allowCodeComponents?.hasAccess) {
      if (runtimeEnvironment === WebflowEnvironments.SSR) {
        return null;
      }
      if (runtimeEnvironment !== WebflowEnvironments.DESIGNER) {
        return (
          <CodeIslandStateCard type="error" attrs={attrs}>
            Code component disabled
          </CodeIslandStateCard>
        );
      }
    }

    if (DesignerMode.READ_ONLY || codeComponentsSafeModeEnabled) {
      return (
        <CodeIslandStateCard type="error" attrs={attrs}>
          Code component disabled
        </CodeIslandStateCard>
      );
    }

    if (!loader || !serializedProps) {
      return (
        <CodeIslandStateCard type="error" attrs={attrs}>
          Invalid configuration
        </CodeIslandStateCard>
      );
    }

    if (loader.tag === 'NULL') {
      return runtimeEnvironment !== WebflowEnvironments.DESIGNER ? null : (
        <CodeIslandStateCard type="error" attrs={attrs}>
          Invalid code component
        </CodeIslandStateCard>
      );
    }

    if (codeIslandState?.status === 'error') {
      const {name, message} = codeIslandState.error;

      if (name === 'CompilationError') {
        return (
          <CodeIslandStateCard
            type="error"
            attrs={attrs}
            layout={isOpenCanvas ? 'vertical' : 'horizontal'}
            action={
              <CodeIslandStateCardFixAction
                onClick={() => requestFixWithAI(message)}
              />
            }
          >
            {t('designer.components.code-component.compilation-errors-note')}
          </CodeIslandStateCard>
        );
      }

      return (
        <CodeIslandStateCard
          type="error"
          attrs={attrs}
          action={
            <CodeIslandStateCardReloadAction
              onClick={handleCodeIslandStateReload}
            />
          }
        >
          Something went wrong
        </CodeIslandStateCard>
      );
    }

    const ssrRendererUrl =
      moduleId && codeModuleWorkerUrlMap
        ? codeModuleWorkerUrlMap[moduleId]?.components
        : undefined;

    const shouldRenderSsr =
      ssrEnabled && // ssr is enabled in code component definition
      ssrRendererUrl && // ssr renderer exist for code component module
      loader.tag === 'FEDERATION' && // only federation loader supported atm
      (!tagStylesEnabled || nestedRootEnabled) && // styles in SSR are only supported with nested root
      supportsStreamingRendering; // ssr is enabled only when streaming rendering is supported

    // Including the per-instance scenario binding in the key
    // force-remounts the `<code-island>` whenever the Designer attaches
    // or changes the binding, so the new value of `data-wf-scenario-id`
    // / `-props` is read by `connectedCallback`. Props are part of the key
    // because `resolveScenarioFromHost` is captured once on connect and
    // the scenario attrs are not in `observedAttributes`, so editing a
    // scenario's seeded values in place (same id) must remount to re-read.
    // Appends nothing when there is no binding (publish/SSR and the
    // default Designer case) so the key stays byte-identical to the
    // pre-multi-state key and no instance needlessly remounts.
    const scenarioKeySegment = scenarioBinding
      ? `-scenario:${scenarioBinding.id}:${
          scenarioBinding.props ? JSON.stringify(scenarioBinding.props) : ''
        }`
      : '';
    const loaderKeySegment =
      loader.tag === 'FEDERATION'
        ? `${loaderString}`
        : `${loaderString}-${siteComponentVersion}`;
    const codeIslandKey = `${loaderKeySegment}-${refreshCounter}${scenarioKeySegment}`;

    return React.createElement(
      'code-island',
      {
        ref: setCodeIslandRef,
        key: codeIslandKey,
        'data-loader': loaderString,
        'data-props': serializedProps,
        'data-slots': serializedSlots,
        'data-hydrate': shouldRenderSsr ? 'true' : 'false',
        'data-webflow-context': webflowContextString,
        'data-interactive': webflowContext.publicContext.interactive,
        'data-instance-id': designerInstanceId,
        // Grid multi-state preview: each grid-cell frame's host
        // carries its own scenario id so the runtime renders only that
        // scenario in this host. Omitted
        // entirely on hosts that are not part of a scenario frame so
        // publish/preview/SSR payloads.
        ...(scenarioBinding
          ? {
              [STATE_SCENARIO_HOST_ATTR]: scenarioBinding.id,
              [STATE_SCENARIO_LABEL_HOST_ATTR]: scenarioBinding.label,
              // Scenario props are stored Designer-side, not in the bundle,
              // so deliver them to the runtime through this host attribute.
              ...(scenarioBinding.props
                ? {
                    [STATE_SCENARIO_PROPS_HOST_ATTR]: JSON.stringify(
                      scenarioBinding.props
                    ),
                  }
                : {}),
            }
          : {}),
        ...analyticsMetadata,
        // display: contents makes the CodeIsland wrapper invisible to layout,
        // so the component renders as if the wrapper doesn't exist. However, this prevents the host
        // element from receiving mouse events, so we implement event bridging in CodeIslandCustomElement
        // to capture events in shadow DOM and re-dispatch them on the host for Designer interactions.
        // In the component canvas (isOpenCanvas), we need the code-island element to participate in layout
        // so that each variant's code component renders within its Frame container
        style: strictCsp
          ? undefined
          : isOpenCanvas && isDirectChildOfCanvasFrame
            ? {display: 'block'}
            : DISPLAY_CONTENTS_STYLE,
        ...attrs,
      },
      <>
        {shouldRenderSsr ? (
          <React.Suspense
            fallback={
              <CodeIslandStateCard type="loading" dimensions={lastKnownSize}>
                {LOADING_MESSAGE}
              </CodeIslandStateCard>
            }
          >
            <CodeIslandSsr
              ssrRendererUrl={ssrRendererUrl}
              serializedProps={serializedProps}
              serializedSlots={serializedSlots}
              componentId={loader.val.submoduleId}
              tagStylesUrl={tagStylesUrl}
              nestedRootEnabled={nestedRootEnabled}
              prerenderEnabled={prerenderEnabled}
              webflowContext={{
                mode: webflowContext.publicContext.mode,
                interactive: webflowContext.publicContext.interactive,
                locale: webflowContext.publicContext.locale,
              }}
            />
          </React.Suspense>
        ) : (
          <ShadowRoot mode="open">
            <>
              {codeIslandLayoutCssFile ? (
                <link rel="stylesheet" href={codeIslandLayoutCssFile} />
              ) : null}
              {nestedRootEnabled ? (
                <div
                  data-root="true"
                  style={strictCsp ? undefined : DISPLAY_CONTENTS_STYLE}
                />
              ) : null}

              {tagStylesUrl ? (
                <link rel="stylesheet" type="text/css" href={tagStylesUrl} />
              ) : null}

              {tagStylesEnabled && tagStyles ? (
                <style id={TAG_CSS_ID}>{tagStyles}</style>
              ) : null}

              {aliasCss ? (
                <style id={VARIABLE_ALIASES_ID}>{aliasCss}</style>
              ) : null}

              {codeIslandState?.status === 'loading' ? (
                <CodeIslandStateCard type="loading" dimensions={lastKnownSize}>
                  {LOADING_MESSAGE}
                </CodeIslandStateCard>
              ) : null}
            </>
          </ShadowRoot>
        )}
        {getChildren(data).map((child) => renderChild(child))}
      </>
    );
  }
);

export const CodeIsland = createIntegratedAtom({
  displayName: 'Code Component',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Code'), iconName('CodeIsland')),
  },
  showEmpty: false,
  fields: {
    loader: withSystem(
      {private: true},
      TUnion({
        FEDERATION: TRecord({
          clientModuleUrl: TText,
          moduleId: TText,
          submoduleId: TText,
          exportPath: TText,
        }),
        SITE: TRecord({
          componentId: TText,
        }),
      })
    ),
    props: withSystem(
      {private: true},
      TGeneric({
        arg: 'PropTypes',
        body: TTypeReference('PropTypes'),
      })
    ),
    options: withSystem(
      {private: true},
      TRecord({
        applyTagSelectors: TBoolean,
        ssr: TBoolean,
        prerender: TBoolean,
      })
    ),
    eventIds: EVENT_IDS_ATOM_FIELD,
    children: TListOfElement,
  },
  autoEngagementTrackingAttribute: true,
  render: CodeIslandRender,
});
